import React, { FC, SyntheticEvent, useEffect, useState } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import Typography, { typographyClasses } from '@mui/material/Typography';
import { TreeItem, treeItemClasses } from '@mui/x-tree-view';
import { TreeView } from '@mui/x-tree-view/TreeView';
import { useSelector } from 'react-redux';

import { IDepartmentDTO, IHierarchicalDepartmentDTO } from 'src/api/dtos';
import { getChildDepartmentIdsToLevel, getDepartmentById } from 'src/features/departments/departments.utils';
import { ChevronDownIcon, ChevronUpIcon, Tooltip } from 'src/shared/components';
import { useTranslate } from 'src/shared/providers';
import { formatDepartmentDisplay } from 'src/shared/utils';
import { getEmployeeDepartmentDisplayFormat } from 'src/store/user/user.selectors';

interface Props {
  selected: IHierarchicalDepartmentDTO | IDepartmentDTO | null | undefined;
  options: IHierarchicalDepartmentDTO[];
  onSelect: (department: IHierarchicalDepartmentDTO) => void;
}

export const DepartmentTreeView: FC<Props> = ({ selected, options, onSelect }) => {
  const { t } = useTranslate();
  const theme = useTheme();
  const employeeDepartmentDisplayFormat = useSelector(getEmployeeDepartmentDisplayFormat);

  const [expanded, setExpanded] = useState<string[]>([]);

  const handleSelect = (event: SyntheticEvent, nodeId: string) => {
    if (!options.length) {
      return;
    }

    if (!(event.target instanceof Element)) {
      return;
    }

    if (
      event.target.classList.contains(treeItemClasses.content) ||
      event.target.classList.contains(treeItemClasses.label) ||
      event.target.classList.contains(typographyClasses.root)
    ) {
      const selected = getDepartmentById(options, nodeId);

      if (!selected) {
        return;
      }

      onSelect(selected);
    }
  };

  const handleToggle = (event: SyntheticEvent, nodeIds: string[]) => {
    if (!(event.target instanceof Element)) {
      return;
    }

    if (
      event.target.classList.contains(treeItemClasses.iconContainer) ||
      event.target.nodeName === 'svg' ||
      event.target.nodeName === 'path'
    ) {
      setExpanded(nodeIds);
    }
  };

  useEffect(() => {
    if (!options?.length) {
      return;
    }

    setExpanded(getChildDepartmentIdsToLevel(options, options.length > 1 ? 0 : 1));
  }, [options]);

  const renderDepartmentOption = (department: IHierarchicalDepartmentDTO) => {
    const label = formatDepartmentDisplay(department.description, department.number, employeeDepartmentDisplayFormat);

    return (
      <StyledTreeItem
        key={department.id}
        nodeId={department.id.toString()}
        label={
          <Tooltip title={<Typography fontSize="0.75rem">{label}</Typography>} showIcon={false}>
            <Typography fontSize="0.75rem" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
              {label}
            </Typography>
          </Tooltip>
        }
      >
        {department.children?.length ? department.children.map((child) => renderDepartmentOption(child)) : null}
      </StyledTreeItem>
    );
  };

  return (
    <TreeView
      selected={selected?.id.toString() ?? null}
      defaultCollapseIcon={<ChevronUpIcon color={theme.palette.action.active} />}
      defaultExpandIcon={<ChevronDownIcon color={theme.palette.action.active} />}
      expanded={expanded}
      onNodeSelect={handleSelect}
      onNodeToggle={handleToggle}
    >
      {options.map((department) => renderDepartmentOption(department))}
      {!options.length && (
        <StyledTreeItem
          disabled
          nodeId=""
          label={<Typography fontSize="0.75rem">{t('global.noOptions')}</Typography>}
        />
      )}
    </TreeView>
  );
};

const StyledTreeItem = styled(TreeItem)(() => ({
  '.MuiTreeItem-content': {
    flexDirection: 'row-reverse',
    padding: '0 12px',
    height: 36,

    '.MuiTreeItem-iconContainer': {
      margin: 0,
      alignItems: 'center',
      height: 36,
      width: 36,
      padding: 0,
    },

    '.MuiTreeItem-label': {
      padding: 0,
      fontSize: 14,
    },
  },
}));
